// The image viewer's geometry — the rules of ios/Sources/Views/ImageViewerView.swift
// (`ZoomableImagePage`): scale between 1× and 6×, a double-tap (here: a click) toggles 1× ↔ 2.5×,
// a zoomed page owns its pan, leaving a page resets its zoom, and the pager walks the post's
// images in order without wrapping (a TabView pager). Pure so the numbers are tested; the web view
// (ui/features/attachments/Lightbox.tsx) only feeds it pointer positions.
//
// Coordinates: `x`/`y` are the image centre's offset from the stage centre, in screen pixels;
// points handed in are relative to the stage centre too.

export const MIN_SCALE = 1;
export const MAX_SCALE = 6;
export const TOGGLE_SCALE = 2.5;

export interface ZoomView {
  scale: number;
  x: number;
  y: number;
}

export interface Size {
  w: number;
  h: number;
}

export const IDENTITY: ZoomView = { scale: 1, x: 0, y: 0 };

export function clampScale(scale: number): number {
  if (!Number.isFinite(scale)) return MIN_SCALE;
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

/**
 * The picture's footprint at 1×: fitted inside the stage keeping its aspect. Unlike the phone
 * (`scaledToFit`, which blows a 64 px icon up to the whole screen) the web never upscales past the
 * picture's own pixels at 1× — the reader can still zoom.
 */
export function fitSize(image: Size, stage: Size): Size {
  if (image.w <= 0 || image.h <= 0 || stage.w <= 0 || stage.h <= 0) return { w: 0, h: 0 };
  const s = Math.min(1, stage.w / image.w, stage.h / image.h);
  return { w: image.w * s, h: image.h * s };
}

/** No pan past the picture's edge: at most half the overflow each way, none when it fits. */
export function clampPan(view: ZoomView, fitted: Size, stage: Size): ZoomView {
  const maxX = Math.max(0, (fitted.w * view.scale - stage.w) / 2);
  const maxY = Math.max(0, (fitted.h * view.scale - stage.h) / 2);
  return { scale: view.scale, x: Math.max(-maxX, Math.min(maxX, view.x)), y: Math.max(-maxY, Math.min(maxY, view.y)) };
}

/** Zoom to `nextScale` keeping the picture point under `point` exactly where it is. */
export function zoomAt(view: ZoomView, nextScale: number, point: { x: number; y: number }, fitted: Size, stage: Size): ZoomView {
  const scale = clampScale(nextScale);
  if (scale === MIN_SCALE) return IDENTITY;
  const k = scale / view.scale;
  return clampPan({ scale, x: point.x - (point.x - view.x) * k, y: point.y - (point.y - view.y) * k }, fitted, stage);
}

/** A click (the phone's double-tap): zoomed → back to 1×; at 1× → 2.5× at the clicked point. */
export function toggle(view: ZoomView, point: { x: number; y: number }, fitted: Size, stage: Size): ZoomView {
  return view.scale > MIN_SCALE ? IDENTITY : zoomAt(view, TOGGLE_SCALE, point, fitted, stage);
}

/** A wheel notch: exponential so trackpad pinches (many small deltas) and mouse notches agree. */
export function wheelScale(scale: number, deltaY: number): number {
  return clampScale(scale * Math.exp(-deltaY * 0.0025));
}

/** The pager: next/previous within the post's images, stopping at either end (no wrap). */
export function step(index: number, delta: number, count: number): number {
  if (count <= 0) return 0;
  return Math.max(0, Math.min(count - 1, index + delta));
}

export const ImageViewerZoom = { MIN_SCALE, MAX_SCALE, TOGGLE_SCALE, IDENTITY, clampScale, fitSize, clampPan, zoomAt, toggle, wheelScale, step } as const;
